import { useEffect } from 'react';
import { useSEO } from '../../../utils/seo';
import { useNavigate } from 'react-router-dom';
import Footer from '../../home/components/Footer';
import WhatsAppButton from '../../home/components/WhatsAppButton';

export default function DRECUYazilimPage() {
  const navigate = useNavigate();

  useSEO({
    title: 'DR.ECU Yazılım Hizmetleri | Denizli Yetkili Bayi',
    description: 'Denizli DR.ECU yetkili bayii. Chip tuning, ECU yazılım optimizasyonu, performans artırma, ekonomi yazılımı, DPF-EGR iptali. Güvenli ve test edilmiş yazılımlar.',
    keywords: 'denizli DR.ECU, denizli chip tuning, denizli ecu yazılım, denizli performans yazılımı, denizli dpf iptali, denizli egr iptali, ecu optimizasyon denizli',
    canonical: '/blog/drecu-yazilim-hizmetleri',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'DR.ECU Yazılım Hizmetleri',
      description: 'Yetkili bayi olarak profesyonel ECU yazılım ve optimizasyon hizmetleri. Chip tuning, performans artırma ve ekonomi yazılımı.',
      author: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/5946f8dc6d1689a139aced3671fb829b/5627d08f05dce4eda6468c71266b611d.png',
        },
      },
      datePublished: '2024-01-15',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/drecu-yazilim-hizmetleri`,
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavigation = (sectionId: string) => {
    navigate(`/#${sectionId}`);
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex-shrink-0 relative" style={{ left: '-5%', top: '5%' }}>
              <button
                onClick={handleLogoClick}
                className="cursor-pointer"
              >
                <img 
                  src="https://static.readdy.ai/image/5946f8dc6d1689a139aced3671fb829b/5627d08f05dce4eda6468c71266b611d.png" 
                  alt="Premium Oto Logo" 
                  className="h-40 sm:h-44 w-auto object-contain"
                />
              </button>
            </div>

            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <button
                onClick={() => handleNavigation('services')}
                className="font-sans text-sm font-medium tracking-wide text-gray-800 hover:text-accent-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                HİZMETLER
              </button>
              <button
                onClick={() => handleNavigation('why-choose-us')}
                className="font-sans text-sm font-medium tracking-wide text-gray-800 hover:text-accent-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                NEDEN BİZ
              </button>
              <button
                onClick={() => handleNavigation('testimonials')}
                className="font-sans text-sm font-medium tracking-wide text-gray-800 hover:text-accent-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                YORUMLAR
              </button>
              <button
                onClick={() => handleNavigation('contact')}
                className="font-sans text-sm font-medium tracking-wide text-gray-800 hover:text-accent-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                İLETİŞİM
              </button>
              <button
                onClick={() => handleNavigation('appointment')}
                className="bg-black text-white px-6 py-2 font-sans text-sm font-medium tracking-wide hover:bg-accent-gold transition-all whitespace-nowrap cursor-pointer"
              >
                RANDEVU AL
              </button>
            </div>

            <button className="md:hidden p-2 cursor-pointer text-black">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/4971c3f6af27d9ff1dfa834bf2e30511.jpg"
          alt="DR.ECU Yazılım Hizmetleri"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl lg:text-6xl font-bold mb-6">DR.ECU Yazılım Hizmetleri</h1>
          <p className="font-sans text-xl text-gray-200">Yetkili bayi olarak profesyonel ECU yazılım ve optimizasyon</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          {/* Introduction */}
          <div className="mb-16">
            <p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
              DR.ECU yetkili bayii olarak, aracınızın motor kontrol ünitesi (ECU) yazılımı konusunda en güncel ve güvenilir hizmetleri sunuyoruz. Modern araçların performansı, yakıt tüketimi ve emisyon değerleri büyük ölçüde ECU yazılımı tarafından belirlenir. Profesyonel yazılım hizmetlerimizle, aracınızın potansiyelini güvenli bir şekilde ortaya çıkarıyoruz.
            </p>
          </div>

          {/* DR.ECU Nedir? */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">DR.ECU Nedir?</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-4">
              DR.ECU, otomotiv sektöründe ECU yazılım çözümleri konusunda dünya çapında tanınan bir markadır. Yetkili bayi olarak sunduğumuz avantajlar:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Orijinal DR.ECU yazılımları ve güncellemeleri</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Üretici garantisi ve teknik destek</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Güvenli ve test edilmiş yazılım haritaları</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Geri dönüşüm garantisi - orijinal yazılıma dönüş imkanı</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Binlerce araç modeli için özel yazılım desteği</span>
              </li>
            </ul>
          </div>

          {/* Image */}
          <div className="mb-16 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=automotive%20ECU%20chip%20tuning%20process%20with%20DR.ECU%20professional%20equipment%2C%20detailed%20view%20of%20ECU%20programming%20interface%20on%20computer%20screen%2C%20technician%20working%20with%20diagnostic%20tools%2C%20modern%20workshop%20setting%20with%20technical%20precision&width=1200&height=600&seq=blog-drecu-002&orientation=landscape"
              alt="ECU Programlama"
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Hizmetlerimiz */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">ECU Yazılım Hizmetlerimiz</h2>
            
            <div className="space-y-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-speed-up-line text-accent-gold text-2xl mr-3"></i>
                  Performans Optimizasyonu (Chip Tuning)
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed mb-3">
                  Aracınızın motor gücünü ve torkunu güvenli sınırlar içinde artırıyoruz. Performans optimizasyonu ile:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Motor gücünde %15-30 artış</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Tork değerinde %20-35 iyileşme</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Daha yumuşak ve lineer güç aktarımı</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Gaz pedalı tepkisinde iyileşme</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-gas-station-line text-accent-gold text-2xl mr-3"></i>
                  Ekonomi Yazılımı
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed mb-3">
                  Yakıt tüketimini azaltmaya odaklanan özel yazılım haritaları. Ekonomi yazılımı ile:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Yakıt tüketiminde %10-20 azalma</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Düşük devirlerde daha iyi tork</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Şehir içi sürüşte verimlilik artışı</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Emisyon değerlerinde iyileşme</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-delete-bin-line text-accent-gold text-2xl mr-3"></i>
                  DPF/EGR/AdBlue İptali
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed mb-3">
                  Dizel araçlarda sorun yaratan emisyon sistemlerinin yazılımsal iptali:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span><strong>DPF İptali:</strong> Partikül filtresi tıkanma sorunlarının çözümü</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span><strong>EGR İptali:</strong> Egzoz gazı geri dönüş sistemi devre dışı</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span><strong>AdBlue İptali:</strong> SCR sistemi yazılımsal devre dışı</span>
                  </li>
                  <li className="font-sans text-sm text-gray-600 flex items-start">
                    <i className="ri-arrow-right-s-line text-accent-gold mr-2 mt-1"></i>
                    <span>Bakım maliyetlerinde önemli azalma</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-speed-line text-accent-gold text-2xl mr-3"></i>
                  Hız Limiti Kaldırma
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Üretici tarafından konulan elektronik hız limitlerinin kaldırılması. Özellikle Alman araçlarda (250 km/s limiti) ve ticari araçlarda uygulanan hız kısıtlamalarının yazılımsal olarak kaldırılması.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-restart-line text-accent-gold text-2xl mr-3"></i>
                  Start-Stop Sistemi İptali
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Şehir içinde rahatsız edici olabilen start-stop sisteminin kalıcı olarak devre dışı bırakılması. Her seferişte manuel kapatma zahmetinden kurtulun.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-error-warning-line text-accent-gold text-2xl mr-3"></i>
                  Hata Kodu Silme ve Kalibrasyon
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Kalıcı hata kodlarının silinmesi, sensör kalibrasyonları ve yazılım güncellemeleri. Gösterge panelinde sürekli yanan arıza lambalarının çözümü.
                </p>
              </div>
            </div>
          </div>

          {/* Süreç */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">ECU Yazılım Süreci</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <div className="bg-accent-gold text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold">1</div>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Ön Değerlendirme</h4>
                <p className="font-sans text-sm text-gray-600">Aracınızın mevcut durumu kontrol edilir, ECU okunur ve hangi yazılımın uygun olduğu belirlenir.</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <div className="bg-accent-gold text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold">2</div>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Yedekleme</h4>
                <p className="font-sans text-sm text-gray-600">Orijinal ECU yazılımı yedeklenir. Bu sayede istendiğinde geri dönüş mümkün olur.</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <div className="bg-accent-gold text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold">3</div>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Yazılım Yükleme</h4>
                <p className="font-sans text-sm text-gray-600">DR.ECU orijinal yazılımı güvenli bir şekilde ECU'ya yüklenir. İşlem 1-2 saat sürer.</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <div className="bg-accent-gold text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold">4</div>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Test ve Teslim</h4>
                <p className="font-sans text-sm text-gray-600">Aracınız test sürüşüne çıkarılır, tüm parametreler kontrol edilir ve size teslim edilir.</p>
              </div>
            </div>
          </div>

          {/* Güvenlik */}
          <div className="mb-16 bg-primary-dark rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-white mb-6">Güvenlik ve Garanti</h2>
            <p className="font-sans text-base text-gray-300 leading-relaxed mb-4">
              ECU yazılımı konusunda güvenlik en önemli önceliğimizdir. DR.ECU yetkili bayii olarak:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Tüm yazılımlar test edilmiş ve onaylanmıştır</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Motor ve şanzıman güvenlik limitleri korunur</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>İstendiğinde orijinal yazılıma geri dönüş ücretsizdir</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Yazılım sonrası teknik destek sağlanır</span>
              </li>
            </ul>
          </div>

          {/* Desteklenen Markalar */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Desteklenen Araç Markaları</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              DR.ECU yazılımları, tüm popüler araç markalarını destekler. Özellikle uzmanlaştığımız markalar:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Mercedes-Benz</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">BMW</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Audi</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Volkswagen</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Porsche</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Ford</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Renault</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Peugeot</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Citroën</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Fiat</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Opel</div>
              <div className="bg-gray-50 p-4 rounded-lg text-center font-sans font-semibold text-gray-900">Toyota</div>
            </div>
            <p className="font-sans text-sm text-gray-600 mt-4 text-center">
              Ve daha fazlası... Aracınızın uyumluluğu için bizimle iletişime geçin.
            </p>
          </div>

          {/* CTA Section */}
          <div className="bg-gray-50 rounded-2xl p-10 text-center">
            <h3 className="font-serif text-3xl font-bold text-gray-900 mb-4">Aracınızın Potansiyelini Keşfedin</h3>
            <p className="font-sans text-base text-gray-700 mb-8 max-w-2xl mx-auto">
              DR.ECU yetkili bayii olarak, aracınız için en uygun yazılım çözümünü sunuyoruz. Ücretsiz danışmanlık için hemen randevu alın.
            </p>
            <a
              href="/#appointment"
              className="inline-block bg-accent-gold text-primary-dark px-8 py-4 rounded-lg font-sans font-semibold hover:bg-primary-dark hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Ücretsiz Danışmanlık Alın
            </a>
          </div>

        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}